import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import TagManager from "react-gtm-module";
import { lazily } from "react-lazily";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import "./App.scss";
import { useGlobalState } from "./GlobalStates";
import { Header } from "./components/header/header";
import DownloadAppFooter from "./components/layout/DownloadAppFooter";
import ScrollToTop from "./customHooks/useScrollToTop";
import { getUserData } from "./services/user";

import axios from "./axios";
import ScrollReveal from "./utils/ScrollReveal";

import posthog from "posthog-js";
import i18n from "./i18n";

import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { AlertBanner } from "./components/elements/AlertBanner";
import Destination from "./intro/Destination";
import LandingNew from "./intro/LandingNew";
import AffiliateProgram from "./pages/affiliate/AffiliateProgram";
import AffiliateTracking from "./pages/affiliate/AffiliateTracking";
import Campaign from "./pages/campaign/Campaign";
import CompanyInformation from "./pages/extras/CompanyInformation";
import SocialLinks from "./pages/extras/SocialLinks";
import Ideas from "./pages/ideas/Ideas";
import SinglesInferno from "./pages/singlesInferno/SinglesInferno";
import Outside from "./pages/externalBrands/Outside";
import { MountKKLanding } from "./pages/extras/MountKKLandingPage/MountKK";

import GroupLeaderInfo from "./pages/leader/GroupLeaderInfo";
import SotravelXInfo from "./pages/sotravelX/SotravelXInfo";
import TestCalendar from "./pages/test-calendar";
import Connections from "./profile/Connections/Connections";
import ImportContacts from "./profile/Connections/ImportContacts";
import Privacy from "./profile/Privacy";

import { countryWithLandingPage } from "./utils/constants";

import { I18nextProvider } from "react-i18next";

let stripePromise;
if (localStorage.getItem("currency") == "myr") {
  stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY_MY);
} else {
  stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
}

const Landing = React.lazy(() => import("./intro/Landing"));
const WelcomeDiscount = React.lazy(() => import("./intro/WelcomeDiscount"));
const WelcomeDiscountV2 = React.lazy(() => import("./intro/WelcomeDiscountV2"));
const CommunityPage = React.lazy(() => import("./intro/CommunityPage"));
const CommunityPageV2 = React.lazy(() => import("./intro/CommunityPageV2"));
const AboutV2 = React.lazy(() => import("./home/AboutV2"));
const PolicyV2 = React.lazy(() =>
  import("./components/privacy-policy/PolicyV2")
);
const TermsAppV2 = React.lazy(() =>
  import("./components/privacy-policy/TermsAppV2")
);
const PolicyAppV2 = React.lazy(() =>
  import("./components/privacy-policy/PolicyAppV2")
);
const HostReviewStatusV2 = React.lazy(() =>
  import("./pages/host/HostReview/HostReviewStatusV2")
);
const HostReviewFormV2 = React.lazy(() =>
  import("./pages/host/HostReview/HostReviewFormV2").then((module) => ({
    default: module.HostReviewFormV2,
  }))
);

const NotFoundV2 = React.lazy(() =>
  import("./pages/NotFoundV2").then((module) => ({
    default: module.NotFoundV2,
  }))
);

const UserProfile = React.lazy(() => import("./profile/UserProfile"));
const MyTripActions = React.lazy(() => import("./myTrips/MyTripActions"));
const MyActivityActions = React.lazy(() =>
  import("./myActivities/MyActivityActions")
);
const MyTripUpload = React.lazy(() => import("./myTrips/MyTripUpload"));
const MyTripItinerary = React.lazy(() => import("./myTrips/MyTripItinerary"));
const MyTripReview = React.lazy(() => import("./myTrips/MyTripReview"));
const MyTripItinerarySubpage = React.lazy(() =>
  import("./myTrips/MyTripItinerarySubpage")
);
// Using lazily for named imports
const { Home } = lazily(() => import("./home/Home"));
const { About } = lazily(() => import("./home/About"));
const { Trip } = lazily(() => import("./pages/trip/Trip"));
const { Book } = lazily(() => import("./pages/reserve/Book"));
const { Create } = lazily(() => import("./pages/reserve/Create"));
const { ClubApp } = lazily(() => import("./home/ClubApp"));
const { Event } = lazily(() => import("./pages/event/Event"));
const { SoclubBook } = lazily(() => import("./pages/reserve/SoclubBook"));

const { Confirm } = lazily(() => import("./pages/booked/Confirm"));
const { ConfirmEventBooking } = lazily(() =>
  import("./pages/booked/ConfirmEventBooking")
);
const { Refund } = lazily(() => import("./pages/booked/Refund"));
const { ProjectS } = lazily(() => import("./projectS/ProjectS"));
const { DoMore } = lazily(() => import("./projectS/DoMore"));

const { LoginPage } = lazily(() => import("./components/login/LoginPage"));
const { AppTelegramLogin } = lazily(() =>
  import("./components/login/AppTelegramLogin")
);
const { AppLineLogin } = lazily(() =>
  import("./components/login/AppLineLogin")
);

//AppLinks
const LinkToAppExplore = React.lazy(() =>
  import("./pages/appLinks/LinkToAppExplore")
);
const LinkToAppSoclub = React.lazy(() =>
  import("./pages/appLinks/LinkToAppSoclub")
);
const LinkToAppChat = React.lazy(() =>
  import("./pages/appLinks/LinkToAppChat")
);
const LinkToReserve = React.lazy(() =>
  import("./pages/appLinks/LinkToReserve")
);
const LinkToAppTripDetails = React.lazy(() =>
  import("./pages/appLinks/LinkToAppTripDetails")
);
const LinkToAppSoclubUserActivityDetails = React.lazy(() =>
  import("./pages/appLinks/LinkToAppSoclubUserActivityDetails")
);
const LinkToAppMerchantBookings = React.lazy(() =>
  import("./pages/appLinks/LinkToAppMerchantBookings")
);

const { LoginForAccess } = lazily(() =>
  import("./components/loginForAccess/LoginForAccess")
);
const { ClaimDiscount } = lazily(() =>
  import("./components/loginForAccess/ClaimDiscount")
);

const { Profile } = lazily(() => import("./profile/Profile"));
const { NotifEdit } = lazily(() => import("./profile/NotifEdit"));
const { Policy } = lazily(() => import("./components/privacy-policy/Policy"));
const { PolicyApp } = lazily(() =>
  import("./components/privacy-policy/PolicyApp")
);
const { TermsApp } = lazily(() =>
  import("./components/privacy-policy/TermsApp")
);

const { MyTrips } = lazily(() => import("./myTrips/MyTrips"));

const { HostForm } = lazily(() => import("./pages/host/HostForm"));
const HostFormV2 = React.lazy(() => import("./pages/host/HostForm/HostFormV2"));
const { HostReviewForm } = lazily(() =>
  import("./pages/host/HostReview/HostReviewForm")
);
const { HostReviewStatus } = lazily(() =>
  import("./pages/host/HostReview/HostReviewStatus")
);

const NotifEditV2 = React.lazy(() =>
  import("./profile/NotifEditV2").then((module) => ({
    default: module.NotifEditV2,
  }))
);

if (process.env.REACT_APP_GTM_ID) {
  TagManager.initialize({ gtmId: process.env.REACT_APP_GTM_ID });
}

function App() {
  if (process.env.NODE_ENV === "development") {
    // init dev stuff
  } else {
    // clarity.init("mmnx5isd28");
    posthog.init("phc_yqvnkXnyjUZacqkVtAbWUGf9TB4ghnziU0CwiM37VMH", {
      api_host: "https://us.i.posthog.com",
      person_profiles: "identified_only", // or 'always' to create profiles for anonymous users as well
    });
  }

  const [user, setUser] = useGlobalState("user");
  const [, setUserLoading] = useGlobalState("userLoading");
  const [promotion, setPromotion] = useGlobalState("promotion");
  const [translations, setTranslations] = useGlobalState("translations");

  const scrollRevealRef = useRef(null);

  // User sign in
  useEffect(() => {
    // Set dev user id and jwt token for convenience

    if (process.env.NODE_ENV === "development") {
      // process.env.REACT_APP_DEV_USER_ID
      //   ? localStorage.setItem("USER_ID", process.env.REACT_APP_DEV_USER_ID)
      //   : localStorage.removeItem("USER_ID");
      // process.env.REACT_APP_DEV_JWT_TOKEN
      //   ? localStorage.setItem("JWT_TOKEN", process.env.REACT_APP_DEV_JWT_TOKEN)
      //   : localStorage.removeItem("JWT_TOKEN");
      process.env.REACT_APP_DEV_USER_ID &&
        localStorage.setItem("USER_ID", process.env.REACT_APP_DEV_USER_ID);
      process.env.REACT_APP_DEV_JWT_TOKEN &&
        localStorage.setItem("JWT_TOKEN", process.env.REACT_APP_DEV_JWT_TOKEN);
    }
    const getUser = async () => {
      let jwt_token = localStorage.getItem("JWT_TOKEN");
      if (jwt_token) {
        try {
          const res = await getUserData();
          setUser(res.data);
          posthog.identify(res.data.id, {
            email: res.data.email,
            name: res.data.first_name,
          });
        } catch (err) {
          console.log("user id does not exist", err);
        }
      }
      setUserLoading(false);
    };

    document.body.classList.add("is-loaded");
    scrollRevealRef.current.init();
    getUser();
  }, [setUser, setUserLoading]);

  useEffect(() => {
    const getCurrentPromotions = async () => {
      const res = await axios.get(`/v1/trip/getOngoingPromotionsForTrips`, {
        params: {
          user_id: user?.id ?? "",
        },
      });
      if (res.data) {
        setPromotion(res.data);
      }
    };

    getCurrentPromotions();
  }, [user]);

  useEffect(() => {
    // init Google Analytics
    ReactGA.initialize([
      {
        trackingId: "G-XL60GVTDED",
      },
    ]);

    scrollRevealRef.current.init();
  }, [window.location.href]);

  // Check for preloaded translations
  useEffect(() => {
    // Initialize translations from window object if available
    if (window.__INITIAL_TRANSLATIONS__) {
      // Since we now have all resources in the translations object,
      // we can directly set them all at once
      setTranslations(window.__INITIAL_TRANSLATIONS__);

      // Log which resources were loaded
      const resources = Object.keys(window.__INITIAL_TRANSLATIONS__);
      const language =
        resources.length > 0
          ? window.__INITIAL_TRANSLATIONS__[resources[0]].language
          : null;
      if (language && language !== "en") {
        console.log(
          `Initialized translations for ${resources.join(", ")} in ${language}`
        );
      }
    }
  }, [setTranslations]);

  return (
    <I18nextProvider i18n={i18n}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ScrollReveal
          ref={scrollRevealRef}
          children={() => (
            <Router>
              <ScrollToTop />
              <AlertBanner />
              <Header />
              <DownloadAppFooter />
              {/* <LoginWidget />
              <LoginModal /> */}
              <Routes>
                <Route path="/" exact element={<LandingNew />} />
                {countryWithLandingPage.map((country) => (
                  <Route path={`/${country}`} exact element={<Destination />} />
                ))}
                <Route
                  path="/welcome-discount"
                  exact
                  element={<WelcomeDiscountV2 />}
                />
                <Route
                  path="/welcome-discount-old"
                  exact
                  element={<WelcomeDiscount />}
                />
                <Route
                  path="/community-page"
                  exact
                  element={<CommunityPageV2 />}
                />
                <Route
                  path="/community-page-old"
                  exact
                  element={<CommunityPage />}
                />
                <Route
                  path="/claim-discount"
                  exact
                  element={<ClaimDiscount />}
                />

                <Route path="/all-trips" exact element={<Home />} />

                <Route path="/about" element={<AboutV2 />} />
                <Route path="/about-old" element={<About />} />

                <Route path="/t/:project_slug" element={<Trip />} />
                <Route path="/trip/:trip_id" element={<Trip />} />
                <Route path="/trips/:project_id" element={<Trip />} />

                <Route path="/host/:host_trip_id" element={<Trip />} />
                <Route path="/reserve" element={<Book />} />
                <Route path="/new" element={<Create />} />
                <Route path="/booked" element={<Confirm />} />
                <Route path="/cancellation" element={<Refund />} />

                <Route path="/club" element={<ClubApp />} />
                <Route path="/club/:activity_id" element={<Event />} />
                <Route path="/soclub/reserve" element={<SoclubBook />} />
                <Route
                  path="/soclub/booked"
                  element={<ConfirmEventBooking />}
                />

                <Route path="/login" element={<LoginPage />} />
                <Route path="/test-calendar" exact element={<TestCalendar />} />

                <Route path="/app-login" element={<AppTelegramLogin />} />
                <Route path="/app-line-login" element={<AppLineLogin />} />

                <Route
                  path="/group-leader-intro"
                  element={<GroupLeaderInfo />}
                />

                <Route path="/sotravel-x" element={<SotravelXInfo />} />

                {/* https://sotravel.com/app-redirect/Explore/trip/141
                {/* https://sotravel.com/app-redirect/Explore/trip/141
              https://sotravel.com/app-redirect/Explore/trips/1 */}
                <Route
                  path="/app-redirect/Explore/*"
                  element={<LinkToAppExplore />}
                />

                <Route
                  path="/app-redirect/Soclub/:merchantStoreId?"
                  element={<LinkToAppSoclub />}
                />
                {/* reserved booking trip page */}
                <Route
                  path="/app-redirect/trip/:tripId?"
                  element={<LinkToAppTripDetails />}
                />
                <Route
                  path="/app-redirect/trip/:tripId?/updateTripInformation"
                  element={
                    <LinkToAppTripDetails subUrl={"updateTripInformation"} />
                  }
                />
                <Route
                  path="/app-redirect/trip/:tripId?/tripInstructions"
                  element={<LinkToAppTripDetails subUrl={"tripInstructions"} />}
                />
                <Route
                  path="/app-redirect/trip/:tripId?/viewRoomies"
                  element={<LinkToAppTripDetails subUrl={"viewRoomies"} />}
                />
                <Route
                  path="/app-redirect/trip/:tripId?/whosComing"
                  element={<LinkToAppTripDetails subUrl={"whosComing"} />}
                />
                <Route
                  path="/app-redirect/trip/:tripId?/settlePayments"
                  element={<LinkToAppTripDetails subUrl={"settlePayments"} />}
                />
                <Route
                  path="/app-redirect/trip/:tripId?/feedback"
                  element={<LinkToAppTripDetails subUrl={"feedback"} />}
                />
                <Route
                  path="/app-redirect/SoclubUserActivityDetails/:activityId?"
                  element={<LinkToAppSoclubUserActivityDetails />}
                />
                <Route
                  path="/app-redirect/chat/:chatId?"
                  element={<LinkToAppChat />}
                />
                <Route
                  path="/app-redirect/reserve"
                  element={<LinkToReserve />}
                />
                <Route
                  path="/app-redirect/event_chat/:chatId?"
                  element={<LinkToAppChat isEventChat={true} />}
                />
                <Route
                  path="/app-redirect/soclub_chat/:chatId?"
                  element={<LinkToAppChat isSoclubChat={true} />}
                />

                <Route
                  path="/app-redirect/MerchantBookings"
                  element={<LinkToAppMerchantBookings />}
                />

                <Route path="/access" element={<LoginForAccess />} />

                <Route path="/profile" element={<Profile />} />
                <Route path="/notif-edit-old" exact element={<NotifEdit />} />
                <Route path="/notif-edit" exact element={<NotifEditV2 />} />
                <Route path="/profile/privacy" element={<Privacy />} />
                <Route path="/profile/connections" element={<Connections />} />
                <Route
                  path="/profile/connections/import-contacts"
                  element={<ImportContacts />}
                />
                <Route path="/profile/:user_id" element={<UserProfile />} />
                <Route
                  path="/profile/:user_id/connections"
                  element={<Connections />}
                />

                <Route path="/project-s" element={<ProjectS />} />
                <Route path="/domore" element={<DoMore />} />

                <Route path="/my-trips" exact element={<MyTrips />} />
                <Route
                  path="/my-trips/:trip_id/action-items"
                  exact
                  element={
                    <Elements stripe={stripePromise}>
                      <MyTripActions />
                    </Elements>
                  }
                />
                <Route
                  path="/my-activities/:activity_id/action-items"
                  exact
                  element={
                    <Elements stripe={stripePromise}>
                      <MyActivityActions />
                    </Elements>
                  }
                />
                <Route
                  path="/my-trips/:trip_id/upload"
                  exact
                  element={<MyTripUpload />}
                />
                <Route
                  path="/my-projects/:project_id/upload"
                  exact
                  element={<MyTripUpload />}
                />

                <Route
                  path="/my-trips/:trip_id/itinerary"
                  exact
                  element={<MyTripItinerary />}
                />
                <Route
                  path="/my-trips/:trip_id/review"
                  exact
                  element={<MyTripReview />}
                />
                <Route
                  path="/my-trips/:trip_id/itinerary/:notion_id"
                  exact
                  element={<MyTripItinerarySubpage />}
                />

                <Route path="/trip-starter-form" element={<HostFormV2 />} />
                <Route path="/trip-starter-form-old" element={<HostForm />} />
                <Route
                  path="/user-review-status"
                  exact
                  element={<HostReviewStatusV2 />}
                />
                <Route
                  path="/add-review/:tele_username"
                  exact
                  element={<HostReviewFormV2 />}
                />
                <Route
                  path="/add-review-old/:tele_username"
                  exact
                  element={<HostReviewForm />}
                />
                <Route
                  path="/user-review-status-old"
                  exact
                  element={<HostReviewStatus />}
                />

                <Route path="/privacy-policy" element={<PolicyV2 />} />
                <Route path="/privacy-policy-old" element={<Policy />} />
                <Route path="/privacy-policy-app" element={<PolicyAppV2 />} />
                <Route path="/privacy-policy-app-old" element={<PolicyApp />} />
                <Route path="/terms-app" element={<TermsAppV2 />} />
                <Route path="/terms-app-old" element={<TermsApp />} />

                <Route path="/ideas" element={<Ideas />} />
                <Route path="/campaign/:campaignSlug" element={<Campaign />} />
                <Route path="/inferno" element={<SinglesInferno />} />

                <Route path="/affiliate" element={<AffiliateProgram />} />
                <Route path="/earnings" element={<AffiliateTracking />} />
                <Route path="/socials" element={<SocialLinks />} />
                <Route
                  path="/company-information"
                  element={<CompanyInformation />}
                />

                <Route element={<Home />} />

                <Route path="/outside" element={<Outside />} />
                <Route path="/mount-kk-2026" element={<MountKKLanding />} />
                {/* Dedicated 404 route */}
                <Route path="/404" element={<NotFoundV2 />} />

                {/* Catch-all route that redirects to the 404 page */}
                <Route
                  path="*"
                  element={
                    <Navigate
                      to="/404"
                      replace
                      state={{
                        from: window.location.pathname + window.location.search,
                      }}
                    />
                  }
                />
              </Routes>
            </Router>
          )}
        />
      </LocalizationProvider>
    </I18nextProvider>
  );
}

export default App;
